import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../utils/supabase';
import { fetchCredits } from '../../utils/api';
import { SUBSCRIPTIONS_ENABLED } from '../../types';
import { NavigationProps } from './types';
import './styles.scss';

const Navigation: React.FC<NavigationProps> = ({
  user,
  setUser,
  setShowAuthModal,
  currentPage,
  setCurrentPage
}) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mobile drawer: lock page scroll while open, close on Escape.
  useEffect(() => {
    if (!drawerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen]);

  // Any navigation closes the drawer.
  useEffect(() => {
    setDrawerOpen(false);
  }, [currentPage]);

  const go = (page: Parameters<typeof setCurrentPage>[0]) => {
    setDrawerOpen(false);
    setShowProfileMenu(false);
    setCurrentPage(page);
  };

  useEffect(() => {
    if (user) {
      loadCredits();
    } else {
      setCredits(null);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user && showProfileMenu) {
      loadCredits();
    }
  }, [user, showProfileMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  // Other pages dispatch `lleo:credits-changed` after an analysis is charged.
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ credits?: number }>).detail;
      if (detail && typeof detail.credits === 'number') {
        setCredits(detail.credits);
      } else if (user) {
        loadCredits();
      }
    };
    window.addEventListener('lleo:credits-changed', onChange);
    return () => window.removeEventListener('lleo:credits-changed', onChange);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCredits = async () => {
    if (!user) return;
    
    try {
      const data = await fetchCredits(user.id);
      setCredits(data?.credits ?? 0);
    } catch (err) {
      console.error('Failed to load credits:', err);
      setCredits(0);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setShowProfileMenu(false);
    setDrawerOpen(false);
  };

  const navItems: { page: 'myanalyses' | 'community' | 'publications' | 'about' | 'pricing'; label: string; short?: string }[] = [
    { page: 'myanalyses', label: 'My Analyses' },
    { page: 'community', label: 'Community Research', short: 'Community' },
    { page: 'publications', label: 'Publications' },
    { page: 'about', label: 'About' },
    ...(SUBSCRIPTIONS_ENABLED ? [{ page: 'pricing' as const, label: 'Pricing' }] : [])
  ];

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-brand" onClick={() => setCurrentPage('home')}>
          <h1>LLEO</h1>
        </div>

        {/* Same links on every screen size. On phones this block wraps onto
            its own row underneath the brand / credits / avatar row. */}
        <div className="navbar-menu" role="navigation" aria-label="Primary">
          {navItems.map((item) => (
            <button
              key={item.page}
              onClick={() => go(item.page)}
              className={`nav-link ${currentPage === item.page ? 'active' : ''}`}
            >
              {item.short ? (
                <>
                  <span className="nav-label-full">{item.label}</span>
                  <span className="nav-label-short">{item.short}</span>
                </>
              ) : (
                item.label
              )}
            </button>
          ))}
        </div>

        <div className="navbar-actions">
          {user && (
            <div className="credits-badge" title="Remaining credits">
              ⚡ {credits !== null ? credits : '...'}
            </div>
          )}
          {user ? (
            <div className="profile-menu-container" ref={profileMenuRef}>
              <button
                className="profile-icon"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <div className="user-avatar">
                  {user.email?.charAt(0).toUpperCase()}
                </div>
              </button>

              {showProfileMenu && (
                <div className="profile-dropdown">
                  <div className="profile-header">
                    <div className="user-avatar-large">
                      {user.email?.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-info">
                      <p className="user-email">{user.email}</p>
                      <p className="user-id">ID: {user.id.slice(0, 8)}...</p>
                    </div>
                  </div>
                  <div className="profile-divider"></div>
                  <div className="credit-display">
                    <span className="credit-label">Credits:</span>
                    <span className="credit-amount">{credits !== null ? credits : '...'}</span>
                  </div>
                  <div className="profile-divider"></div>
                  <button onClick={() => setCurrentPage('settings')} className="dropdown-item">
                    Settings
                  </button>
                  <button onClick={() => setCurrentPage('billing')} className="dropdown-item">
                    Billing
                  </button>
                  <div className="profile-divider"></div>
                  <button onClick={handleLogout} className="dropdown-item logout">
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => setShowAuthModal(true)} className="btn btn-neutral">
              Login / Sign Up
            </button>
          )}

          {/* Phones only (hidden by CSS on desktop). Right-most so it sits
              under the thumb, next to the avatar — logo left, menu right. */}
          <button
            type="button"
            className={`nav-hamburger ${drawerOpen ? 'open' : ''}`}
            onClick={() => setDrawerOpen((v) => !v)}
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={drawerOpen}
            aria-controls="nav-drawer"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Mobile drawer: slides in from the right over a dimmed backdrop. */}
      <div
        className={`nav-drawer-backdrop ${drawerOpen ? 'open' : ''}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />
      <aside
        id="nav-drawer"
        className={`nav-drawer ${drawerOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        aria-hidden={!drawerOpen}
      >
        <div className="nav-drawer-head">
          <span className="nav-drawer-brand" onClick={() => go('home')}>LLEO</span>
          <button
            type="button"
            className="nav-drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        {user ? (
          <div className="nav-drawer-user">
            <div className="user-avatar">{user.email?.charAt(0).toUpperCase()}</div>
            <div className="nav-drawer-user-info">
              <p className="nav-drawer-email">{user.email}</p>
              <p className="nav-drawer-credits">⚡ {credits !== null ? credits : '...'} credits</p>
            </div>
          </div>
        ) : (
          <div className="nav-drawer-user">
            <button
              className="btn btn-neutral nav-drawer-login"
              onClick={() => {
                setDrawerOpen(false);
                setShowAuthModal(true);
              }}
            >
              Login / Sign Up
            </button>
          </div>
        )}

        <nav className="nav-drawer-links" aria-label="Menu">
          <button onClick={() => go('home')} className={`nav-drawer-link ${currentPage === 'home' ? 'active' : ''}`}>
            Home
          </button>
          {navItems.map((item) => (
            <button
              key={item.page}
              onClick={() => go(item.page)}
              className={`nav-drawer-link ${currentPage === item.page ? 'active' : ''}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {user && (
          <>
            <div className="nav-drawer-divider" />
            <nav className="nav-drawer-links" aria-label="Account">
              <button onClick={() => go('settings')} className={`nav-drawer-link ${currentPage === 'settings' ? 'active' : ''}`}>
                Settings
              </button>
              <button onClick={() => go('billing')} className={`nav-drawer-link ${currentPage === 'billing' ? 'active' : ''}`}>
                Billing
              </button>
              <button onClick={handleLogout} className="nav-drawer-link logout">
                Logout
              </button>
            </nav>
          </>
        )}
      </aside>
    </nav>
  );
};

export default Navigation;
